<template>
  <div
    id="screenshotArea"
    class="relative flex h-screen w-screen overflow-hidden md:min-h-[50rem] md:min-w-[50rem] bg-saudi-dark "
  >
    <img
      class="absolute h-screen w-screen object-cover min-h-[50rem] min-w-[50rem]"
      :src="stadiums[selectedStadiumId - 1]?.inImage || defaultBackground"
      :style="{ opacity: imageOpacity }"
      @load="onImageLoad"
    />

    <div
      v-if="loading"
      class="absolute left-0 top-0 z-[60] flex h-screen w-screen items-center justify-center bg-black opacity-70"
    >
      <div class="spinner"></div>
    </div>

    <div
      v-if="screenshot && !loading"
      class="absolute left-0 top-0 z-[60] flex h-screen w-screen items-start justify-center border-2 drop-shadow-lg"
    >
      <div class="absolute z-[50] h-screen w-screen bg-black opacity-50"></div>
      <img
        :src="screenshot"
        alt="Screenshot"
        class="relative top-[5%] z-[80] max-h-[80%] max-w-[90%] rounded-xl border-2 drop-shadow-md border-gray-400 object-contain"
      />
      <div
        class="absolute bottom-[3%] z-[100] flex items-center justify-center space-x-4"
      >
        <SmallButton
          icon="arrow.png"
          imageStyle="w-[2.5vw] md:w-[1vw]"
          @click="handleReturn"
        />
        <SmallButton
          icon="download.png"
          imageStyle="w-[5vw] md:w-[2vw] md:mb-1"
          @click="downloadImage"
        />
        <div class="block md:hidden">
          <SmallButton
            icon="share.png"
            imageStyle="w-[4.5vw]"
            @click="shareFiles"
          />
        </div>
      </div>
    </div>

    <div
      v-if="!screenshot"
      class="absolute md:right-[-24vw] right-[5vw] w-[25%] scale-[1.8]  md:scale-[2.4] bottom-[8vh]"
    >
      <img class="md:w-[8vw]" src="../assets/logo.png" />
    </div>

    <div :class="screenshot ? 'hidden' : 'block'">
      <div class="relative flex h-screen w-screen">
        <div class="absolute w-full h-full hidden md:block">
          <img
            src="../assets/banners/nameBanner3.svg"
            class="absolute top-0 left-[12.5vw] w-[90%] h-[40vh] object-fill"
            alt="Banner"
          />
          <div
            class="absolute top-0 left-[12.5vw] w-full h-[17.5vh] text-[3vh] flex items-center justify-center"
          >
            <h1 class="text-white text-[2.6vw] font-bold">
              {{ squadName.toUpperCase() }}
            </h1>
          </div>
        </div>

        <div class="absolute w-full h-full md:hidden">
          <div
            class="flex justify-center w-full h-[5%] mt-[2vh] items-center bg-[#539e48] border-white"
          >
            <h1 class="font-bold text-white text-[5.2vw]">
              {{ squadName.toUpperCase() }}
            </h1>
          </div>
        </div>

        <div class="hidden h-full w-[35%] items-center md:flex">
          <SideBar
            :formation="formation"
            :squadName="squadName"
            @updateFormation="handleFormationUpdate"
            @updateSquadName="handleSquadNameUpdate"
            @updateStadium="handleStadiumUpdate"
            @updateBlockButton="handleBlockButton"
            @handleScreenshot="hideArrow"
            :stadiums="stadiums"
            :isTransitioning="isTransitioning"
            :selectedStadiumId="selectedStadiumId"
            :isMobile="false"
            :blockButton="blockButton"
          />
        </div>

        <div
          class="absolute top-[10%] h-[95%] w-[95%] items-center md:hidden"
          :class="!showSidebar ? 'left-[-90.5%]' : 'left-0'"
          style="transition-duration: 1s"
        >
          <SideBar
            :formation="formation"
            :squadName="squadName"
            @updateFormation="handleFormationUpdate"
            @updateSquadName="handleSquadNameUpdate"
            @updateStadium="handleStadiumUpdate"
            @updateBlockButton="handleBlockButton"
            @handleScreenshot="hideArrow"
            @toggleSidebar="toggleSideBar"
            :stadiums="stadiums"
            :isTransitioning="isTransitioning"
            :selectedStadiumId="selectedStadiumId"
            :isMobile="true"
            :blockButton="blockButton"
          />
          <div
            @click="toggleSideBar()"
            class="absolute right-[-4%] top-0 z-[60] flex h-[4vh] w-[4vh] items-center justify-center rounded-full bg-saudi-dark transition-transform duration-700"
            :class="{ 'rotate-180': !showSidebar, hidden: !showSidebarArrow }"
          >
            <button class="flex items-center justify-center">
              <img class="w-[20%] mr-[5%]" src="../assets/icons/arrow.png" />
            </button>
          </div>
        </div>

        <div
          :style="{ pointerEvents: blockButton ? 'none' : 'auto' }"
          class="flex h-full w-full justify-center"
        >
          <PitchHolder :formation="formation" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import SideBar from "./SideBar.vue";
import PitchHolder from "./PitchHolder.vue";
import html2canvas from "html2canvas-pro";
import defaultBackground from "../assets/defaultBackground.jpg";
import stadiumData from "../data/stadiums.json";
import SmallButton from "../button/SmallButton.vue";
import { useI18n } from "vue-i18n";


const isMobile = ref(window.innerWidth < 768);
const { locale } = useI18n();

window.onload = trackIframeVisit;

const updateWindowWidth = () => {
  isMobile.value = window.innerWidth < 768;
};

const share = ref(
  locale.value === "ar"
    ? {
        text: "ورينا تشكيلتك السعودية المثالية للمونديال  https://bid.livemena.dev/iframe/",
      }
    : {
        text: "Here’s my Saudi Arabian Ultimate XI build yours! https://bid.livemena.dev/iframe/",
      }
);

const stadiums = stadiumData.map(({ id, enName, arName }) => ({
  id,
  enName,
  arName,
  exImage: require(`../assets/stadiums/exterior/stadium${id}X.jpg`),
  inImage: require(`../assets/stadiums/interior/stadium${id}.jpg`),
}));

const formation = ref("442");
const squadName = ref("");
const selectedStadiumId = ref(0);
const showSidebar = ref(false);
const imageOpacity = ref(1);
const screenshot = ref(null);
const loading = ref(false);
const isTransitioning = ref(false);
const showSidebarArrow = ref(true);
const blockButton = ref(false);

onMounted(() => {
  window.addEventListener("resize", updateWindowWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWindowWidth);
});

const shareFiles = async () => {
  if (screenshot.value) {
    const response = await fetch(screenshot.value);
    const blob = await response.blob();
    const file = new File([blob], "screenshot.png", { type: "image/png" });

    const canShare =
      navigator.canShare && navigator.canShare({ files: [file] });

    try {
      if (canShare) {
        await navigator.share({
          title: share.value.title,
          text: share.value.text,
          files: [file],
        });
      }
    } catch (e) {
      console.error("Error sharing:", e);
    }
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", "share_button_click", {
      value: 1,
    });
  }
};

const downloadImage = () => {
  const link = document.createElement("a");
  link.href = screenshot.value;
  link.download = "screenshot.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (typeof window.gtag === "function") {
    window.gtag("event", "download_button_click", {
      value: 1,
    });
  }
};

function getUniqueVisitorId() {
  let visitorId = localStorage.getItem("uniqueVisitorId");
  if (!visitorId) {
    visitorId = "visitor_" + Date.now();
    localStorage.setItem("uniqueVisitorId", visitorId);
  }
  return visitorId;
}

function trackIframeVisit() {
  const visitorId = getUniqueVisitorId();

  if (typeof window.gtag === "function") {
    window.gtag("event", "iframe_visit", {
      event_category: "Iframe",
      event_label: visitorId,
      value: 1,
    });
  }
}

const handleBlockButton = (value) => {
  blockButton.value = value;
};

const handleFormationUpdate = (newFormation) => {
  formation.value = newFormation;
};

const handleSquadNameUpdate = (newSquadName) => {
  squadName.value = newSquadName;
};

const handleStadiumUpdate = (stadiumId) => {
  if (!isTransitioning.value && selectedStadiumId.value !== stadiumId) {
    fadeOutAndChangeStadium(stadiumId);
  }
};

const fadeOutAndChangeStadium = (stadiumId) => {
  isTransitioning.value = true;
  imageOpacity.value = 0;

  setTimeout(() => {
    selectedStadiumId.value = stadiumId;
    imageOpacity.value = 1;

    setTimeout(() => {
      isTransitioning.value = false;
    }, 750);
  }, 750);
};

const toggleSideBar = () => {
  showSidebar.value = !showSidebar.value;
};

const hideArrow = () => {
  showSidebarArrow.value = false;

  setTimeout(() => {
    handleScreenshot();
  }, 750);
};

const handleReturn = () => {
  showSidebarArrow.value = true;
  screenshot.value = null;

  if (typeof window.gtag === "function") {
    window.gtag("event", "return_button_click", {
      value: 1,
    });
  }
};

const handleScreenshot = () => {
  loading.value = true;
  const isMobile = window.innerWidth < 768;
  const screenshotArea = isMobile
    ? document.body
    : document.querySelector("#screenshotArea");

  html2canvas(screenshotArea, {
    useCORS: true,
    allowTaint: true,
  })
    .then((canvas) => {
      const croppedCanvas = document.createElement("canvas");
      const ctx = croppedCanvas.getContext("2d");

      let captureWidth, captureHeight, captureLeft;

      if (isMobile) {
        captureWidth = canvas.width;
        captureHeight = canvas.height;
        captureLeft = 0;
      } else {
        captureWidth = canvas.width / 1.4;
        captureHeight = canvas.height;
        captureLeft = canvas.width / 3.6;
      }

      croppedCanvas.width = captureWidth;
      croppedCanvas.height = captureHeight;

      ctx.drawImage(
        canvas,
        captureLeft,
        0,
        captureWidth,
        captureHeight,
        0,
        0,
        captureWidth,
        captureHeight
      );

      screenshot.value = croppedCanvas.toDataURL("image/png");

      setTimeout(() => {
        loading.value = false;
      }, 1000);
    })
    .catch(() => {
      loading.value = false;
    });
};
</script>

<style scoped>
#screenshotArea img {
  transition: opacity 1s ease-in-out;
}

.spinner {
  border: 8px solid rgba(255, 255, 255, 0.3);
  border-top: 8px solid rgb(4, 188, 4);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
